import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaBaby, FaUserTie, FaMapMarkerAlt, FaBirthdayCake, FaMoneyBillWave, FaUser, FaEnvelope, FaPhone, FaCheck, FaLock } from 'react-icons/fa';
import { MdWork, MdLocationCity, MdAccessTime, MdCheckCircle } from 'react-icons/md';
import { loginWithEmail, loginWithGoogle, registerWithPassword } from '../services/api';
import { useToast } from '../components/ToastContainer';
import ToastContainer from '../components/ToastContainer';
import ThemeToggle from '../components/ThemeToggle';
import AuthLayout from '../components/AuthLayout';
import { t, getLanguage } from '../utils/i18n';

const DEFAULT_CITY_OPTIONS = ['Beograd', 'Novi Sad', 'Niš', 'Kragujevac', 'Subotica', 'Zrenjanin', 'Pančevo', 'Čačak', 'Kraljevo', 'Smederevo'];

export default function Register() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    role: null,
    isLookingForBabysitter: true,
    jobSearchStatus: null,
    cities: [],
    customCityOptions: [],
    customCity: '',
    childBirthYear: null,
    hourlyRateMin: '',
    hourlyRateMax: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setLang] = useState(getLanguage());
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, removeToast, toasts } = useToast();
  const redirectAfterRegister = location.state?.redirect;

  useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const normalizeCity = (value) => value.trim().toLowerCase();

  const formatCityName = (value) =>
    value
      .trim()
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const handleAddCustomCity = () => {
    const trimmed = formData.customCity.trim();

    if (!trimmed) {
      showToast(t('auth.register.city.enterCity'), 'warning', 3000);
      return;
    }

    const formatted = formatCityName(trimmed);
    const normalized = normalizeCity(formatted);

    const defaultMatch = DEFAULT_CITY_OPTIONS.find((city) => normalizeCity(city) === normalized);
    const customMatch = formData.customCityOptions.find((city) => normalizeCity(city) === normalized);
    const cityToAdd = defaultMatch || customMatch || formatted;

    const alreadySelected = formData.cities.some((city) => normalizeCity(city) === normalized);
    const nextCities = alreadySelected
      ? formData.cities
      : [...formData.cities, cityToAdd];

    const nextCustomOptions = defaultMatch || customMatch
      ? formData.customCityOptions
      : [...formData.customCityOptions, formatted];

    setFormData({
      ...formData,
      customCityOptions: nextCustomOptions,
      cities: nextCities,
      customCity: '',
    });

    if (alreadySelected) {
      showToast(t('auth.register.city.alreadyAdded'), 'info', 2000);
    }
  };

  const renderCitySelection = (title, description) => {
    const customCityOptions = formData.customCityOptions || [];
    const cityOptions = [...DEFAULT_CITY_OPTIONS];

    customCityOptions.forEach((city) => {
      if (!cityOptions.some((option) => normalizeCity(option) === normalizeCity(city))) {
        cityOptions.push(city);
      }
    });

    const toggleCitySelection = (city) => {
      const normalized = normalizeCity(city);
      const isSelected = formData.cities.some((selectedCity) => normalizeCity(selectedCity) === normalized);

      if (isSelected) {
        setFormData({
          ...formData,
          cities: formData.cities.filter((selectedCity) => normalizeCity(selectedCity) !== normalized),
        });
      } else {
        setFormData({
          ...formData,
          cities: [...formData.cities, city],
        });
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <MdLocationCity className="text-3xl text-primary-600 dark:text-primary-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{description}</p>
        <div className="flex flex-wrap gap-2">
          {cityOptions.map((city) => {
            const isSelected = formData.cities.some((selectedCity) => normalizeCity(selectedCity) === normalizeCity(city));

            return (
              <button
                key={city}
                type="button"
                onClick={() => toggleCitySelection(city)}
                className={`flex items-center justify-between gap-2 px-4 py-2 text-sm rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800'
                }`}
              >
                <span>{city}</span>
                {isSelected && <FaCheck className="text-primary-600 dark:text-primary-300" />}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4 w-fit">
          <div className="relative flex-1">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={formData.customCity}
              onChange={(e) => setFormData({...formData, customCity: e.target.value})}
              placeholder={t('auth.register.city.addPlaceholder')}
              className="w-full px-4 py-2 pl-10 pr-24 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            />
            <button
              type="button"
              onClick={handleAddCustomCity}
              className="absolute right-1 top-1 bottom-1 px-3 text-sm font-medium rounded-md border-1 border-primary-700 text-primary-700 hover:bg-primary-100 transition-colors"
            >
              {t('auth.register.city.addButton')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const STEP_KEYS = {
    ROLE: 'role',
    SEEKING: 'seeking',
    CITY_PRIMARY: 'city_primary',
    CHILD_YEAR: 'child_year',
    CITY_MULTI: 'city_multi',
    HOURLY: 'hourly',
    NAME: 'name',
    EMAIL: 'email',
    PASSWORD: 'password',
  };

  const getStepName = (stepKey) => {
    const names = {
      [STEP_KEYS.ROLE]: 'Uloga',
      [STEP_KEYS.SEEKING]: 'Preferencije',
      [STEP_KEYS.CITY_PRIMARY]: 'Lokacija',
      [STEP_KEYS.CITY_MULTI]: 'Lokacija',
      [STEP_KEYS.CHILD_YEAR]: 'Osnovni podaci',
      [STEP_KEYS.HOURLY]: 'Osnovni podaci',
      [STEP_KEYS.NAME]: 'Osnovni podaci',
      [STEP_KEYS.EMAIL]: 'Nalog',
      [STEP_KEYS.PASSWORD]: 'Lozinka',
    };
    return names[stepKey] || '';
  };

  // Calculate steps dynamically based on role and search status
  const getSteps = () => {
    const baseSteps = [STEP_KEYS.ROLE, STEP_KEYS.SEEKING];

    if (!formData.role) {
      return [...baseSteps, STEP_KEYS.NAME, STEP_KEYS.EMAIL, STEP_KEYS.PASSWORD];
    }

    if (formData.role === 'babysitter') {
      return [...baseSteps, STEP_KEYS.CITY_MULTI, STEP_KEYS.HOURLY, STEP_KEYS.NAME, STEP_KEYS.EMAIL, STEP_KEYS.PASSWORD];
    }

    // parent
    if (formData.isLookingForBabysitter) {
      return [...baseSteps, STEP_KEYS.CITY_MULTI, STEP_KEYS.CHILD_YEAR, STEP_KEYS.NAME, STEP_KEYS.EMAIL, STEP_KEYS.PASSWORD];
    }

    return [...baseSteps, STEP_KEYS.CHILD_YEAR, STEP_KEYS.NAME, STEP_KEYS.EMAIL, STEP_KEYS.PASSWORD];
  };
  
  const steps = getSteps();

  const handleNext = () => {
    const currentStepKey = steps[currentStep];

    if (currentStepKey === STEP_KEYS.ROLE && !formData.role) {
      showToast(t('auth.register.selectRole'), 'warning', 3000);
      return;
    }

    if (currentStepKey === STEP_KEYS.SEEKING && formData.role === 'babysitter' && !formData.jobSearchStatus) {
      showToast(t('auth.register.selectJobStatus'), 'warning', 3000);
      return;
    }

    if (
      (currentStepKey === STEP_KEYS.CITY_PRIMARY || currentStepKey === STEP_KEYS.CITY_MULTI) &&
      formData.cities.length === 0
    ) {
      showToast(t('auth.register.city.selectAtLeastOne'), 'warning', 3000);
      return;
    }

    if (currentStepKey === STEP_KEYS.PASSWORD) {
      const p = (formData.password || '').trim();
      const c = (formData.confirmPassword || '').trim();
      if (p.length < 6) {
        showToast(t('auth.register.password.minLength') || 'Lozinka mora imati najmanje 6 karaktera', 'warning', 3000);
        return;
      }
      if (p !== c) {
        showToast(t('auth.register.password.mismatch') || 'Lozinke se ne poklapaju', 'warning', 3000);
        return;
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getRegistrationData = () => {
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      phone: formData.phone,
      cities: formData.cities,
      customCityOptions: formData.customCityOptions,
      childBirthYear: formData.childBirthYear,
      hourlyRateMin: formData.hourlyRateMin,
      hourlyRateMax: formData.hourlyRateMax,
      jobSearchStatus: formData.jobSearchStatus,
      isLookingForBabysitter: formData.isLookingForBabysitter,
    };
  };

  const handleGoogleSignUp = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    
    // Check if clientId is missing or is a placeholder value
    const isPlaceholder = !clientId || 
      clientId === 'YOUR_GOOGLE_CLIENT_ID' || 
      clientId.toLowerCase() === 'your-google-client-id' ||
      clientId.toLowerCase().includes('your-google') ||
      !clientId.includes('.apps.googleusercontent.com');
    
    if (isPlaceholder) {
      console.warn('Google Client ID not configured:', clientId);
      showToast(t('auth.login.googleNotConfigured'), 'info', 6000);
      return;
    }

    // Save registration data to sessionStorage before Google Sign In
    const registrationData = getRegistrationData();
    sessionStorage.setItem('googleRegistrationData', JSON.stringify(registrationData));

    try {
      // Load Google Identity Services
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Google script load timeout')), 10000);
        });
      }

      // Initialize Google Sign In callback (credential-based flow for ID tokens)
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            // Get registration data from sessionStorage
            const savedRegistrationData = sessionStorage.getItem('googleRegistrationData');
            const registrationData = savedRegistrationData ? JSON.parse(savedRegistrationData) : null;
            sessionStorage.removeItem('googleRegistrationData');

            // Decode the credential to get user info
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            const idToken = response.credential;

            // Get user info from the JWT payload
            const email = payload.email;
            const displayName = payload.name;
            const photoUrl = payload.picture;

            // Call backend with the ID token and registration data
            const result = await loginWithGoogle(idToken, '', email, displayName, photoUrl, registrationData);
            if (result.success) {
              showToast(t('auth.register.registrationSuccess'), 'success', 2000);
              const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterRegister || '/dashboard');
              setTimeout(() => {
                navigate(destination, { replace: true });
              }, 500);
            } else {
              showToast(result.error || t('auth.register.googleError'), 'error', 6000);
            }
          } catch (error) {
            showToast(t('auth.register.googleError'), 'error', 6000);
            console.error('Google Sign Up callback error:', error);
          }
        },
      });

      // Create a hidden container and render Google button, then trigger it programmatically
      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.visibility = 'hidden';
      document.body.appendChild(buttonContainer);
      
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signup_with',
      });
      
      // Trigger the button click after it's rendered
      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) {
          googleButton.click();
        } else {
          // Fallback to prompt
          window.google.accounts.id.prompt();
        }
        // Clean up
        setTimeout(() => {
          document.body.removeChild(buttonContainer);
        }, 1000);
      }, 100);
    } catch (error) {
      console.error('Google Sign Up error:', error);
      showToast(t('auth.register.googleError'), 'error', 6000);
      sessionStorage.removeItem('googleRegistrationData');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lastStepKey = steps[steps.length - 1];
    if (lastStepKey === STEP_KEYS.PASSWORD) {
      const p = (formData.password || '').trim();
      const c = (formData.confirmPassword || '').trim();
      if (p.length < 6) {
        showToast(t('auth.register.password.minLength') || 'Lozinka mora imati najmanje 6 karaktera', 'warning', 3000);
        return;
      }
      if (p !== c) {
        showToast(t('auth.register.password.mismatch') || 'Lozinke se ne poklapaju', 'warning', 3000);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (lastStepKey === STEP_KEYS.PASSWORD && formData.password) {
        // Register with password (email + password flow)
        const reg = getRegistrationData();
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email.trim(),
          password: formData.password.trim(),
          role: formData.role || 'parent',
          phone: formData.phone || '',
          address: { cities: reg.cities || [] },
          preferences: {
            cities: reg.cities || [],
            customCityOptions: reg.customCityOptions,
            childBirthYear: reg.childBirthYear,
            hourlyRateMin: reg.hourlyRateMin,
            hourlyRateMax: reg.hourlyRateMax,
            jobSearchStatus: reg.jobSearchStatus,
            isLookingForBabysitter: reg.isLookingForBabysitter,
          },
        };
        const result = await registerWithPassword(payload);
        if (result.success) {
          showToast(t('auth.register.registrationSuccess'), 'success', 2000);
          const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterRegister || '/dashboard');
          setTimeout(() => navigate(destination, { replace: true }), 500);
        } else {
          showToast(result.error || t('common.error'), 'error', 5000);
        }
      } else {
        // Email link flow (no password step in flow)
        const registrationData = getRegistrationData();
        const result = await loginWithEmail(formData.email, registrationData);
        if (result.success) {
          showToast(t('auth.register.checkEmail'), 'success', 6000);
          setTimeout(() => navigate('/login', { state: redirectAfterRegister ? { redirect: redirectAfterRegister } : undefined }), 2000);
        } else {
          showToast(t('common.error') + ': ' + result.error, 'error', 5000);
        }
      }
    } catch (error) {
      showToast(t('common.error') + ': ' + (error.message || 'Greška'), 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    const currentStepKey = steps[currentStep];

    switch (currentStepKey) {
      case STEP_KEYS.ROLE:
        return (
          <div className="space-y-4">
            <div className="mb-6">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('auth.register.role.title') || 'Izaberite vašu ulogu'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Izaberite ulogu – kasnije možete dodati i drugu.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({...formData, role: 'parent'})}
                className={`relative p-6 rounded-[22px] border-2 transition-all text-left group ${
                  formData.role === 'parent'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md ring-2 ring-primary-200 dark:ring-primary-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                {formData.role === 'parent' && (
                  <div className="absolute top-3 right-3">
                    <MdCheckCircle className="text-2xl text-primary-600 dark:text-primary-400" />
                  </div>
                )}
                <div className="flex items-center mb-3">
                  <FaBaby className="text-4xl text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('auth.register.role.parent.title') || 'Roditelj / Staratelj'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('auth.register.role.parent.description') || 'Tražite dadilju za vaše dete'}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, role: 'babysitter'})}
                className={`relative p-6 rounded-[22px] border-2 transition-all text-left group ${
                  formData.role === 'babysitter'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md ring-2 ring-primary-200 dark:ring-primary-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                {formData.role === 'babysitter' && (
                  <div className="absolute top-3 right-3">
                    <MdCheckCircle className="text-2xl text-primary-600 dark:text-primary-400" />
                  </div>
                )}
                <div className="flex items-center mb-3">
                  <FaUserTie className="text-4xl text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {t('auth.register.role.babysitter.title') || 'Dadilja / Babysitter'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('auth.register.role.babysitter.description') || 'Tražite posao kao dadilja'}
                </p>
              </button>
            </div>
          </div>
        );

      case STEP_KEYS.SEEKING:
        if (formData.role === 'babysitter') {
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <MdWork className="text-3xl text-primary-600 dark:text-primary-400" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth.register.jobSearch.title')}</h3>
              </div>
              <div className="space-y-3">
                {['full_time', 'additional', 'not_looking'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFormData({
                      ...formData,
                      jobSearchStatus: status,
                      isLookingForBabysitter: status !== 'not_looking',
                    })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      formData.jobSearchStatus === status
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                    }`}
                  >
                    <span className="text-gray-900 dark:text-gray-100">
                      {status === 'full_time' && t('auth.register.jobSearch.fullTime')}
                      {status === 'additional' && t('auth.register.jobSearch.additional')}
                      {status === 'not_looking' && t('auth.register.jobSearch.notLooking')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FaBaby className="text-3xl text-primary-600 dark:text-primary-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth.register.seeking.title')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('auth.register.seeking.description')}</p>
            <div className="space-y-3">
              <button
                onClick={() => setFormData({...formData, isLookingForBabysitter: true})}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  formData.isLookingForBabysitter
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                }`}
              >
                <FaBaby className={`text-xl ${formData.isLookingForBabysitter ? 'text-primary-600' : 'text-gray-400'}`} />
                <span className="text-gray-900 dark:text-gray-100">{t('auth.register.seeking.yes')}</span>
              </button>
              <button
                onClick={() => setFormData({...formData, isLookingForBabysitter: false})}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  !formData.isLookingForBabysitter
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                }`}
              >
                <MdAccessTime className={`text-xl ${!formData.isLookingForBabysitter ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <span className="text-gray-900 dark:text-gray-100">{t('auth.register.seeking.no')}</span>
              </button>
            </div>
          </div>
        );

      case STEP_KEYS.CITY_PRIMARY:
        return renderCitySelection(
          t('auth.register.city.titleProvider'),
          t('auth.register.city.descriptionProvider')
        );

      case STEP_KEYS.CITY_MULTI:
        return renderCitySelection(
          formData.role === 'babysitter'
            ? t('auth.register.city.titleProvider')
            : t('auth.register.city.titleParent'),
          formData.role === 'babysitter'
            ? t('auth.register.city.descriptionProvider')
            : t('auth.register.city.descriptionParent')
        );

      case STEP_KEYS.CHILD_YEAR:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FaBirthdayCake className="text-3xl text-primary-600 dark:text-primary-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth.register.childYear.title')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('auth.register.childYear.description')}</p>
            <select
              value={formData.childBirthYear || ''}
              onChange={(e) => setFormData({...formData, childBirthYear: e.target.value})}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">{t('auth.register.childYear.select')}</option>
              {Array.from({length: 20}, (_, i) => new Date().getFullYear() - i).map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        );

      case STEP_KEYS.HOURLY:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FaMoneyBillWave className="text-3xl text-primary-600 dark:text-primary-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth.register.hourly.title')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('auth.register.hourly.description')}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">{t('auth.register.hourly.min')}</label>
                <input
                  type="number"
                  value={formData.hourlyRateMin}
                  onChange={(e) => setFormData({...formData, hourlyRateMin: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={t('auth.register.hourly.minPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">{t('auth.register.hourly.max')}</label>
                <input
                  type="number"
                  value={formData.hourlyRateMax}
                  onChange={(e) => setFormData({...formData, hourlyRateMax: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={t('auth.register.hourly.maxPlaceholder')}
                />
              </div>
            </div>
          </div>
        );

      case STEP_KEYS.NAME:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FaUser className="text-3xl text-primary-600 dark:text-primary-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('auth.register.name.title')}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{t('auth.register.name.description')}</p>
            <div className="space-y-4">
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder={t('auth.register.name.firstName')}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder={t('auth.register.name.lastName')}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        );

      case STEP_KEYS.PASSWORD:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FaLock className="text-3xl text-primary-600 dark:text-primary-400" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('auth.register.password.title') || 'Izaberite lozinku'}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('auth.register.password.description') || 'Lozinka mora imati najmanje 6 karaktera.'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
                  {t('auth.register.password.label') || 'Lozinka'}
                </label>
                <div className="relative">
                  <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={t('auth.register.password.placeholder') || '••••••••'}
                    className="input-base pl-10"
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
                  {t('auth.register.password.confirmLabel') || 'Potvrdi lozinku'}
                </label>
                <div className="relative">
                  <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder={t('auth.register.password.confirmPlaceholder') || '••••••••'}
                    className="input-base pl-10"
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case STEP_KEYS.EMAIL:
        return (
          <div className="space-y-4">
            <div className="mb-6">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('auth.register.email.title') || 'Kreirajte nalog'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('auth.register.email.description') || 'Unesite email i telefon za kreiranje naloga'}
              </p>
            </div>
            
            {/* Google Sign Up Option */}
            <button
              type="button"
              onClick={handleGoogleSignUp}
              className="w-full flex items-center justify-center gap-3 px-4 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-[15px] hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium shadow-sm mb-4"
            >
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5"
              />
              <span>{t('auth.register.googleSignUp') || 'Registrujte se sa Google nalogom'}</span>
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">ili</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
                  Email
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('auth.register.email.emailPlaceholder') || 'tvoj@email.com'}
                    className="input-base pl-10"
                    required
                  />
                </div>
                <p className="helper-text">
                  Poslaćemo vam link za aktivaciju naloga
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">
                  Telefon (opciono)
                </label>
                <div className="relative">
                  <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder={t('auth.register.email.phonePlaceholder') || '+381 60 123 4567'}
                    className="input-base pl-10"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AuthLayout title="Registracija" subtitle="Kreirajte nalog i počnite da koristite Nannity">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      <div className="card max-w-xl mx-auto">
        {/* Progress Bar with Step Name */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('auth.register.step')} {currentStep + 1} {t('auth.register.of')} {steps.length}
              </span>
              {steps[currentStep] && (
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                  • {getStepName(steps[currentStep])}
                </span>
              )}
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

            <form onSubmit={currentStep === steps.length - 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('auth.register.back') || 'Nazad'}
                </button>
                {currentStep < steps.length - 1 ? (
                  <button
                    type="submit"
                    disabled={steps[currentStep] === STEP_KEYS.ROLE && !formData.role}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('auth.register.next') || 'Dalje'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !formData.email ||
                      (steps[steps.length - 1] === STEP_KEYS.PASSWORD &&
                        (!(formData.password || '').trim() ||
                          formData.password !== formData.confirmPassword))
                    }
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('auth.register.submit.sending') || 'Šalje se...' : t('auth.register.submit.register') || 'Registruj se'}
                  </button>
                )}
              </div>
            </form>
            
        {/* Sign in link below form */}
        <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('auth.register.alreadyHaveAccount')?.split('?')[0] || 'Već imate nalog'}?{' '}
            <Link 
              to="/login" 
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors font-semibold"
            >
              {t('auth.register.signInLink') || 'Prijavite se'}
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}


